/**
 * <swatch-dot>: paints a colour dot from the colour's own product photo.
 *
 * The rules that read the photo are the KANZE app's (assets/swatch-color.js), so a colour
 * looks the same in the app, on a product card and on the product page.
 *
 * Order of trust for one dot:
 *   1. a colour or picture saved on the option value in Shopify: a person chose it, and
 *      Liquid prints it (snippets/swatch-style.liquid). No <swatch-dot> is printed then.
 *   2. the colour worked out from the photo;
 *   3. the typical shade for the colour's name, or neutral grey.
 *
 * A dot is painted at once with the name's shade (or what this phone worked out last time),
 * so it is never a tiny photo and never flashes. When it is near the screen, and the browser
 * has a quiet moment, the photo is read at 96px wide and the dot takes its real shade. The
 * result is remembered on the phone per photo, so each photo is read once.
 *
 * A name with two colours ("Black - Yellow") gets a dot split in two halves.
 *
 * Markup, printed by snippets/swatch-dot.liquid inside the element that draws the dot:
 *   <swatch-dot data-name="Navy Blue" data-image="/cdn/shop/files/x.jpg?v=1&width=96" data-text="pants Too Essential Trousers"></swatch-dot>
 */
import { fallbackHexForName, familyOf, isBottomGarment, pickSwatchColor, toHsv } from './swatch-color.js';

const CACHE_PREFIX = 'kz-sw:';
const AT_ONCE = 4;
const NEAR_SCREEN = '300px';

/** "#1a2b3c" → [26, 43, 60] */
const toRgb = (hex) => [1, 3, 5].map((at) => parseInt(hex.slice(at, at + 2), 16));

/**
 * The colours a name asks for: one, or two when it names two colours with a separator
 * between them ("Black - Yellow", "Navy / White"). "Navy Blue" is one colour.
 * @param {string} name
 * @returns {string[]} The part of the name for each colour
 */
export function colourParts(name) {
  const parts = String(name || '').split(/\s+[-–]\s+|\s*[/&+]\s*|\s+and\s+/i).map((part) => part.trim()).filter(Boolean);
  const known = parts.filter((part) => familyOf(part));
  const families = new Set(known.map((part) => familyOf(part)));
  return known.length >= 2 && families.size >= 2 ? known.slice(0, 2) : [String(name || '')];
}

/**
 * The dot's colours from one photo. The first colour follows the whole name, as in the app.
 * A second colour is the part of the photo that fits its own name, or that name's typical shade.
 */
export function coloursFromPhoto(pixels, name, bottom) {
  const parts = colourParts(name);
  const first = pickSwatchColor(pixels, name, bottom);
  if (!first) return null;
  if (parts.length < 2) return [first];
  const family = familyOf(parts[1]);
  const second = pickSwatchColor(pixels, parts[1], bottom);
  return [first, second && family.is(...toHsv(toRgb(second))) ? second : family.typical];
}

/** The shades to show before any photo is read. */
export const coloursFromName = (name) => colourParts(name).map((part) => fallbackHexForName(part));

// ─── The page part ────────────────────────────────────────────────────────────

if (typeof window !== 'undefined' && typeof HTMLElement !== 'undefined') {
  const waiting = [];
  let reading = 0;
  const idle = window.requestIdleCallback ? (work) => window.requestIdleCallback(work, { timeout: 1500 }) : (work) => window.setTimeout(work, 80);

  /** At most a few photos at a time, each started in a quiet moment. */
  const next = () => {
    if (reading >= AT_ONCE || !waiting.length) return;
    const dot = waiting.shift();
    reading += 1;
    idle(() => {
      dot.read().finally(() => {
        reading -= 1;
        next();
      });
    });
    next();
  };

  const watcher =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (!entry.isIntersecting) continue;
              watcher.unobserve(entry.target);
              const dot = entry.target.querySelector(':scope > swatch-dot');
              if (dot) {
                waiting.push(dot);
                next();
              }
            }
          },
          { rootMargin: NEAR_SCREEN }
        )
      : null;

  const remembered = (key) => {
    try {
      const saved = JSON.parse(window.localStorage.getItem(key));
      return Array.isArray(saved) && saved.every((hex) => /^#[0-9a-f]{6}$/.test(hex)) ? saved : null;
    } catch (error) {
      return null;
    }
  };

  class SwatchDot extends HTMLElement {
    connectedCallback() {
      this.target = this.parentElement;
      if (!this.target) return;
      this.name = this.dataset.name || '';
      this.key = '';
      if (this.dataset.image) {
        const address = new URL(this.dataset.image, window.location.href);
        this.address = address;
        // One answer per photo (its address and version), colour name and garment part.
        this.key = `${CACHE_PREFIX}${address.pathname}?v=${address.searchParams.get('v') || ''}|${this.name.toLowerCase()}|${isBottomGarment(this.dataset.text || '') ? 1 : 0}`;
      }
      const saved = this.key ? remembered(this.key) : null;
      this.paint(saved || coloursFromName(this.name), saved ? 'remembered' : 'name');
      if (saved || !this.address || !watcher) return;
      watcher.observe(this.target);
    }

    disconnectedCallback() {
      if (this.target) watcher?.unobserve(this.target);
    }

    /** One colour fills the dot. Two colours split it in halves. */
    paint(colours, source) {
      this.target.style.setProperty('--dot-color', colours[0]);
      if (colours.length > 1) this.target.style.setProperty('--dot-image', `conic-gradient(${colours[0]} 0 50%, ${colours[1]} 50% 100%)`);
      else this.target.style.removeProperty('--dot-image');
      this.dataset.source = source;
      this.dataset.colours = colours.join(',');
      // For whoever names the colour from its shade (assets/kz-siblings.js).
      this.dispatchEvent(new CustomEvent('swatch:paint', { bubbles: true, detail: { colours, source } }));
    }

    async read() {
      if (!this.isConnected) return;
      try {
        const photo = new Image();
        // The store's own /cdn/shop/… address is same-origin. Anything else needs permission to be read.
        if (this.address.origin !== window.location.origin) photo.crossOrigin = 'anonymous';
        photo.decoding = 'async';
        photo.src = this.address.href;
        await photo.decode();
        const width = photo.naturalWidth;
        const height = photo.naturalHeight;
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext('2d', { willReadFrequently: true });
        context.drawImage(photo, 0, 0);
        const rgba = context.getImageData(0, 0, width, height).data;
        const data = new Uint8Array(width * height * 3);
        for (let from = 0, to = 0; from < rgba.length; from += 4, to += 3) {
          data[to] = rgba[from];
          data[to + 1] = rgba[from + 1];
          data[to + 2] = rgba[from + 2];
        }
        const colours = coloursFromPhoto({ data, width, height }, this.name, isBottomGarment(this.dataset.text || ''));
        if (!colours || !this.isConnected) return;
        this.paint(colours, 'photo');
        try {
          window.localStorage.setItem(this.key, JSON.stringify(colours));
        } catch (error) {
          /* storage is blocked or full: the photo is simply read again next time */
        }
      } catch (error) {
        /* the photo could not be read: the dot keeps the shade for its name */
        this.dataset.source = 'name (photo not read)';
      }
    }
  }

  if (!window.customElements.get('swatch-dot')) window.customElements.define('swatch-dot', SwatchDot);
}
